import React, { useEffect, useMemo, useState, useCallback, useDeferredValue } from 'react'
import { Row, Col, Spinner, Container } from 'react-bootstrap'
import useFetch from '../../../_fetch'
import { ClickDataTable } from './click-table'
import { formatNumberLocale, formatNumber, formatLocalDateForInput, getLocalDateStart, getLocalDateEnd } from "../../../_utils"
import { useAuth } from '../../../auth/auth-context'
import { ClicksPerPath,IPublisher,Infos,ITransaction, ImpressionsPerPath, IPartnership } from './types'
import LinkKpiOverview from './link-kpi-overview'
import DateRangeSelector from '../home/dashboard-content/date-range-selector'
import { loadPref, resolveLabel } from '../home/dashboard-content/date-range-preference'
import DomainPathFilter from './domain-path-filter'
import { DomainPathFilterSelection, isFilterActive, loadFilterPref } from './domain-path-filter-preference'

type DashboardPageRow = {
    domain: string
    pathId: number
    path: string
    tracked: number
    untracked: number
    total: number
    totalTransactions: number
    totalCommission: number
    impressions: number
}

type DashboardLinkRow = {
    linkId: number
    url: string
    NPClicks: number
    Clicks: number
    totalTransactions: number
    totalCommission: number
}

type DashboardCampaignRow = {
    campaignId: number | string
    name: string
    network: string
    publisherName: string
    clicks: number
    totalTransactions: number
    totalCommission: number
}

type DailyClickRow = {
    date: string
    linkId: number
    pathId: number
    clicks: number
}
/*
RPM
Page views
Total clicks
*/
const getDrillLevel = (prefix: string | null): number => {
    if (!prefix) return 1
    const firstSlash = prefix.indexOf('/')
    if (firstSlash === -1) return 1
    const pathPart = prefix.slice(firstSlash)
    return pathPart.split('/').filter(Boolean).length + 1
}

const buildBreadcrumbs = (prefix: string | null): { label: string; prefix: string | null }[] => {
    const crumbs: { label: string; prefix: string | null }[] = [{ label: 'Overview', prefix: null }]
    if (!prefix) return crumbs
    const firstSlash = prefix.indexOf('/')
    if (firstSlash === -1) return crumbs
    const domain = prefix.slice(0, firstSlash)
    const pathPart = prefix.slice(firstSlash)
    const segments = pathPart.split('/').filter(Boolean)
    let pathSoFar = ''
    for (const seg of segments) {
        pathSoFar += '/' + seg
        crumbs.push({ label: '/' + seg + '/', prefix: domain + pathSoFar + '/' })
    }
    return crumbs
}

const cleanLabel = (value: unknown, fallback = 'Unknown'): string => {
    if (typeof value !== 'string') return fallback
    const normalized = value.trim()
    if (!normalized) return fallback
    const lowered = normalized.toLowerCase()
    if (lowered === 'undefined' || lowered === 'null' || lowered === 'nan') return fallback
    return normalized
}

function parseLocalDate(value: string | null | undefined, endOfDay = false): Date | null {
    if (!value) return null
    const parsed = new Date(`${value}${endOfDay ? 'T23:59:59.999' : 'T00:00:00.000'}`)
    return Number.isFinite(parsed.getTime()) ? parsed : null
}

function isPartnershipActiveOnDate(partnership: IPartnership, date: string) {
    const clickDate = parseLocalDate(date)
    const partnershipStart = parseLocalDate(partnership.startDate)
    const partnershipEnd = parseLocalDate(partnership.expiryDate, true)

    if (!clickDate) return false
    if (partnershipStart && partnershipStart > clickDate) return false
    if (partnershipEnd && partnershipEnd < clickDate) return false

    return true
}

function normalizeDomain(value: string | null | undefined) {
    if (!value) return ''
    const trimmed = value.trim().toLowerCase()
    if (!trimmed) return ''

    try {
        return new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`).hostname.replace(/^www\./, '')
    } catch {
        return trimmed
            .replace(/^https?:\/\//, '')
            .split('/')[0]
            .split(':')[0]
            .replace(/^www\./, '')
    }
}

function getPageKey(domain: string, path: string) {
    return `${domain}\u0000${path}`
}

function addNumber(map: Map<string, number>, key: string, value: number) {
    map.set(key, (map.get(key) || 0) + value)
}

function addNumberById(map: Map<number, number>, key: number, value: number) {
    map.set(key, (map.get(key) || 0) + value)
}

const LinkScriptDashboard: React.FC<{publishers: IPublisher[]}> = ({ publishers }) => {
    const { doFetch } = useFetch()
    const { user } = useAuth()

    const [infos, setInfos] = useState<Pick<Infos, 'domains' | 'paths'>>({ domains: [], paths: [] })

    const [filterSelection, setFilterSelection] = useState<DomainPathFilterSelection>(() => loadFilterPref())

    const [serverPageRows, setServerPageRows] = useState<DashboardPageRow[]>([])
    const [serverLinkRows, setServerLinkRows] = useState<DashboardLinkRow[]>([])
    const [serverCampaignRows, setServerCampaignRows] = useState<DashboardCampaignRow[]>([])
    const [serverDailyClicks, setServerDailyClicks] = useState<DailyClickRow[]>([])
    const [serverKpiClicksPerPath, setServerKpiClicksPerPath] = useState<ClicksPerPath[]>([])
    const [serverKpiBadClicksPerPath, setServerKpiBadClicksPerPath] = useState<ClicksPerPath[]>([])
    const [serverKpiImpressionsPerPath, setServerKpiImpressionsPerPath] = useState<ImpressionsPerPath[]>([])
    const [serverKpiTransactions, setServerKpiTransactions] = useState<ITransaction[]>([])
    const [partnerships, setPartnerships] = useState<IPartnership[]>([])

    const [subPathDrillPrefix, setSubPathDrillPrefix] = useState<string | null>(null)
    const deferredDrillPrefix = useDeferredValue(subPathDrillPrefix)
    const isSubPathTransitioning = deferredDrillPrefix !== subPathDrillPrefix
    const subPathDrillLevel = useMemo(() => getDrillLevel(subPathDrillPrefix), [subPathDrillPrefix])
    

    const [loading, setLoading] = useState(true)

    const [fromDate, setFromDate] = useState(() => {
        const saved = resolveLabel(loadPref('link-script'))
        if (saved) return formatLocalDateForInput(saved.startDate)
        const fromD = new Date()
        fromD.setDate(fromD.getDate() - 30)
        return formatLocalDateForInput(fromD)
    })

    const [toDate, setToDate] = useState(() => {
        const saved = resolveLabel(loadPref('link-script'))
        if (saved) return formatLocalDateForInput(saved.endDate)
        return formatLocalDateForInput(new Date())
    })

    const fetchClicks = useCallback(async (from?: string, to?: string, selectionOverride?: DomainPathFilterSelection) => {
        setLoading(true)
        try {
            const finalFrom = getLocalDateStart(from || fromDate)
            const finalTo = getLocalDateEnd(to || toDate)
            const selection = selectionOverride || filterSelection
            let clickPartnerships: IPartnership[] = []

            if (user?.kind === 'publisher' && user?.partnerships?.enabled) {
                const resPartnerships = await doFetch('/byelink/partnerships?type=click')
                if (resPartnerships.status < 400) {
                    const data = await resPartnerships.json()
                    clickPartnerships = Array.isArray(data) ? data : []
                    setPartnerships(clickPartnerships)
                } else {
                    setPartnerships([])
                }
            } else {
                setPartnerships([])
            }

            const resClicks = await doFetch('/byelink/link-script-dashboard', {
                fromDate: finalFrom.toISOString(),
                toDate: finalTo.toISOString(),
                filterSelection: selection,
                includeDailyClicks: clickPartnerships.length > 0,
            })

            if (resClicks.status >= 400) {
                throw await resClicks.text()
            }

            const clicksJson = await resClicks.json()

            const nextInfos = clicksJson?.infos || { domains: [], paths: [] }
            setInfos({ domains: nextInfos.domains || [], paths: nextInfos.paths || [] })

            setServerPageRows(clicksJson?.pageRows || [])
            setServerLinkRows(clicksJson?.linkRows || [])
            setServerCampaignRows(clicksJson?.campaignRows || [])
            setServerDailyClicks(clicksJson?.dailyClicks || clicksJson?.dailyAutomatedClicks || [])

            setServerKpiClicksPerPath(clicksJson?.kpis?.clicksPerPath || [])
            setServerKpiBadClicksPerPath(clicksJson?.kpis?.badClicksPerPath || [])
            setServerKpiImpressionsPerPath(clicksJson?.kpis?.impressionsPerPath || [])
            setServerKpiTransactions(clicksJson?.kpis?.transactions || [])

        } catch (err) {
            console.error('Error fetching clicks:', err)
        } finally {
            setLoading(false)
        }
    }, [doFetch, fromDate, toDate, filterSelection, user?.kind, user?.partnerships?.enabled])

    useEffect(() => {
        const fetchData = async () => {
            try {
                await fetchClicks()
            } catch (err) {
                console.error('Error fetching initial data:', err)
                setLoading(false)
            }
        }
        fetchData()
    }, [fetchClicks])

    // Returns true if a domain+path combo passes the active filter (prefix-match)
    const isRowAllowed = useCallback((domainStr: string, pathStr: string): boolean => {
        if (!isFilterActive(filterSelection)) return true
        const matchedDomain = infos.domains.find(d => d.domain === domainStr)
        if (!matchedDomain) return false
        const selectedDomainIds = Object.keys(filterSelection.domains).map(Number)
        if (!selectedDomainIds.includes(matchedDomain.id)) return false
        const selectedPrefixes = filterSelection.domains[matchedDomain.id]
        // empty = all paths for this domain
        if (!selectedPrefixes || selectedPrefixes.length === 0) return true
        // prefix-match: pathStr must start with one of the selected top-level prefixes
        return selectedPrefixes.some(prefix => pathStr.startsWith(prefix))
    }, [filterSelection, infos.domains])

    const cpcAdjustments = useMemo(() => {
        const clickPartnershipsByDomain = new Map<string, IPartnership[]>()

        for (const partnership of partnerships) {
            if (partnership.type !== 'click') continue
            const value = Number(partnership.dashboardValue ?? partnership.value) || 0
            if (value <= 0) continue

            for (const domain of partnership.domains || []) {
                const normalizedDomain = normalizeDomain(domain)
                if (!normalizedDomain) continue
                const current = clickPartnershipsByDomain.get(normalizedDomain) || []
                current.push(partnership)
                clickPartnershipsByDomain.set(normalizedDomain, current)
            }
        }

        const pageCommissionByKey = new Map<string, number>()
        const linkCommissionByUrl = new Map<string, number>()
        const partnershipCommissionById = new Map<string, number>()
        const qualifyingClicksByPageKey = new Map<string, number>()
        const qualifyingClicksByLinkUrl = new Map<string, number>()
        const qualifyingClicksByPartnershipId = new Map<string, number>()
        const promotedClicksByPathId = new Map<number, number>()
        const pageByPathId = new Map<number, DashboardPageRow>()
        const linkByLinkId = new Map<number, DashboardLinkRow>()
        let total = 0

        for (const row of serverPageRows) {
            pageByPathId.set(Number(row.pathId), row)
        }

        for (const row of serverLinkRows) {
            linkByLinkId.set(Number(row.linkId), row)
        }

        for (const row of serverDailyClicks) {
            const link = linkByLinkId.get(Number(row.linkId))
            const page = pageByPathId.get(Number(row.pathId))
            if (!link || !page) continue

            const targetDomain = normalizeDomain(link.url)
            const matchingPartnerships = clickPartnershipsByDomain.get(targetDomain)
            if (!matchingPartnerships?.length) continue

            for (const partnership of matchingPartnerships) {
                if (!isPartnershipActiveOnDate(partnership, row.date)) continue

                const commission = (Number(row.clicks) || 0) * (Number(partnership.dashboardValue ?? partnership.value) || 0)
                if (commission <= 0) continue

                const clicks = Number(row.clicks) || 0
                const pageKey = getPageKey(page.domain, page.path)
                addNumber(pageCommissionByKey, pageKey, commission)
                addNumber(linkCommissionByUrl, link.url, commission)
                addNumber(partnershipCommissionById, partnership.id, commission)
                addNumber(qualifyingClicksByPageKey, pageKey, clicks)
                addNumber(qualifyingClicksByLinkUrl, link.url, clicks)
                addNumber(qualifyingClicksByPartnershipId, partnership.id, clicks)
                total += commission
            }
        }

        const promotedClicksByPageKey = new Map<string, number>()
        const promotedClicksByLinkUrl = new Map<string, number>()

        for (const row of serverPageRows) {
            const pageKey = getPageKey(row.domain, row.path)
            const qualifyingClicks = qualifyingClicksByPageKey.get(pageKey) || 0
            const alreadyAutomated = Number(row.tracked) || 0
            const untracked = Number(row.untracked) || 0
            const promoted = Math.min(untracked, Math.max(0, qualifyingClicks - alreadyAutomated))
            if (promoted > 0) {
                promotedClicksByPageKey.set(pageKey, promoted)
                addNumberById(promotedClicksByPathId, Number(row.pathId), promoted)
            }
        }

        for (const row of serverLinkRows) {
            const qualifyingClicks = qualifyingClicksByLinkUrl.get(row.url) || 0
            const alreadyAutomated = Number(row.NPClicks) || 0
            const untracked = Number(row.Clicks) || 0
            const promoted = Math.min(untracked, Math.max(0, qualifyingClicks - alreadyAutomated))
            if (promoted > 0) promotedClicksByLinkUrl.set(row.url, promoted)
        }

        const clickCampaignRows = partnerships
            .filter((partnership) => partnership.type === 'click')
            .map((partnership, index): DashboardCampaignRow | null => {
                const clicks = qualifyingClicksByPartnershipId.get(partnership.id) || 0
                const totalCommission = partnershipCommissionById.get(partnership.id) || 0
                if (clicks <= 0 && totalCommission <= 0) return null

                return {
                    campaignId: `click-${partnership.id || index}`,
                    name: cleanLabel(partnership.name, 'Click partnership'),
                    network: 'Nordpar',
                    publisherName: 'Click agreement',
                    clicks,
                    totalTransactions: 0,
                    totalCommission,
                }
            })
            .filter((row): row is DashboardCampaignRow => row !== null)

        return {
            pageCommissionByKey,
            linkCommissionByUrl,
            promotedClicksByPageKey,
            promotedClicksByLinkUrl,
            promotedClicksByPathId,
            clickCampaignRows,
            total,
        }
    }, [partnerships, serverDailyClicks, serverLinkRows, serverPageRows])

    const pageOverviewRows = useMemo(() => {
        return serverPageRows.map((row) => {
            const pageKey = getPageKey(row.domain, row.path)
            const promotedClicks = cpcAdjustments.promotedClicksByPageKey.get(pageKey) || 0

            return {
                ...row,
                tracked: row.tracked + promotedClicks,
                untracked: Math.max(0, row.untracked - promotedClicks),
                totalCommission: row.totalCommission + (cpcAdjustments.pageCommissionByKey.get(pageKey) || 0),
            }
        })
    }, [cpcAdjustments, serverPageRows])

    const filteredPathOverview = useMemo(() => {
        return pageOverviewRows.filter(row => isRowAllowed(row.domain, row.path))
    }, [pageOverviewRows, isRowAllowed])

    const adjustedKpiClicksPerPath = useMemo(() => {
        const rowsByPathId = new Map<number, ClicksPerPath>()

        for (const row of serverKpiClicksPerPath) {
            rowsByPathId.set(Number(row.path_id), { ...row })
        }

        for (const [pathId, promotedClicks] of Array.from(cpcAdjustments.promotedClicksByPathId.entries())) {
            const existing = rowsByPathId.get(pathId) || { path_id: pathId, clicks: 0 }
            existing.clicks = (Number(existing.clicks) || 0) + promotedClicks
            rowsByPathId.set(pathId, existing)
        }

        return Array.from(rowsByPathId.values())
    }, [cpcAdjustments.promotedClicksByPathId, serverKpiClicksPerPath])

    const adjustedKpiBadClicksPerPath = useMemo(() => {
        return serverKpiBadClicksPerPath.map((row) => {
            const promotedClicks = cpcAdjustments.promotedClicksByPathId.get(Number(row.path_id)) || 0
            return {
                ...row,
                clicks: Math.max(0, (Number(row.clicks) || 0) - promotedClicks),
            }
        })
    }, [cpcAdjustments.promotedClicksByPathId, serverKpiBadClicksPerPath])

    const domainOverview = useMemo(() => {
        const domainMap = new Map<string, {
            domain: string
            tracked: number
            untracked: number
            total: number
            totalTransactions: number
            totalCommission: number
            impressions: number
        }>()

        for (const row of filteredPathOverview) {
            const current = domainMap.get(row.domain) || {
                domain: row.domain,
                tracked: 0,
                untracked: 0,
                total: 0,
                totalTransactions: 0,
                totalCommission: 0,
                impressions: 0,
            }

            current.tracked += row.tracked || 0
            current.untracked += row.untracked || 0
            current.total += row.total || 0
            current.totalTransactions += row.totalTransactions || 0
            current.totalCommission += row.totalCommission || 0
            current.impressions += row.impressions || 0

            domainMap.set(row.domain, current)
        }

        return Array.from(domainMap.values())
    }, [filteredPathOverview])

    const filteredDomainOverview = domainOverview.filter(d => d.total > 0 || d.tracked > 0 || d.impressions > 0 || d.totalTransactions > 0 || d.totalCommission > 0)

    const subPathOverview = useMemo(() => {
        const level = getDrillLevel(deferredDrillPrefix)
        if (level > 4) return []

        const groupedRows = new Map<string, {
            page: string
            tracked: number
            untracked: number
            total: number
            totalTransactions: number
            totalCommission: number
            impressions: number
        }>()

        for (const row of filteredPathOverview) {
            const fullKey = `${row.domain}${row.path}`
            if (deferredDrillPrefix && !fullKey.startsWith(deferredDrillPrefix)) continue

            const pathSegments = row.path.split('/').filter(Boolean)
            if (pathSegments.length < level) continue

            const groupedPath = '/' + pathSegments.slice(0, level).join('/') + '/'
            const page = `${row.domain}${groupedPath}`

            const existing = groupedRows.get(page) || {
                page, tracked: 0, untracked: 0, total: 0,
                totalTransactions: 0, totalCommission: 0, impressions: 0
            }
            existing.tracked += row.tracked
            existing.untracked += row.untracked
            existing.total += row.total
            existing.totalTransactions += row.totalTransactions
            existing.totalCommission += row.totalCommission
            existing.impressions += row.impressions
            groupedRows.set(page, existing)
        }

        return Array.from(groupedRows.values())
    }, [filteredPathOverview, deferredDrillPrefix])

    useEffect(() => {
        if (!subPathDrillPrefix) return
        const hasMatch = filteredPathOverview.some(row => `${row.domain}${row.path}`.startsWith(subPathDrillPrefix))
        if (!hasMatch) {
            setSubPathDrillPrefix(null)
        }
    }, [subPathDrillPrefix, filteredPathOverview])
    const linkOverviewRows = useMemo(() => {
        return serverLinkRows.map((row) => {
            const promotedClicks = cpcAdjustments.promotedClicksByLinkUrl.get(row.url) || 0

            return {
                ...row,
                NPClicks: row.NPClicks + promotedClicks,
                Clicks: Math.max(0, row.Clicks - promotedClicks),
                totalCommission: row.totalCommission + (cpcAdjustments.linkCommissionByUrl.get(row.url) || 0),
            }
        })
    }, [cpcAdjustments, serverLinkRows])

    const campaignOverviewRows = useMemo(() => {
        const publisherByInternalId = new Map<number, IPublisher>()
        for (const publisher of publishers) {
            publisherByInternalId.set(publisher.internalId, publisher)
        }

        const existingRows = serverCampaignRows.map((row) => {
            const publisher = publisherByInternalId.get(Number(row.campaignId))
            return {
                ...row,
                name: cleanLabel(publisher?.name, cleanLabel(row.name)),
                network: cleanLabel(publisher?.network, cleanLabel(row.network)),
                publisherName: cleanLabel(publisher?.publisherName, cleanLabel(row.publisherName)),
            }
        })

        return [...existingRows, ...cpcAdjustments.clickCampaignRows]
    }, [cpcAdjustments.clickCampaignRows, publishers, serverCampaignRows])


    return (
        <Container fluid>
            <div className="link-script-header mb-4">
                <div>
                    <h3 className="mb-0">Link automation</h3>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div className="link-script-date-selector">
                        <DateRangeSelector
                            pageKey="link-script"
                            onSubmit={async (startDate, endDate) => {
                                const nextFromDate = formatLocalDateForInput(startDate)
                                const nextToDate = formatLocalDateForInput(endDate)

                                setFromDate(nextFromDate)
                                setToDate(nextToDate)
                                await fetchClicks(nextFromDate, nextToDate)
                            }}
                        />
                    </div>
                    <DomainPathFilter
                        domains={infos.domains}
                        paths={infos.paths}
                        value={filterSelection}
                        onApply={async (selection) => {
                            setFilterSelection(selection)
                            setSubPathDrillPrefix(null)
                            await fetchClicks(undefined, undefined, selection)
                        }}
                    />
                </div>
            </div>

            <hr />

            {loading && serverPageRows.length === 0 ? (
                <Row>
                    <Col className="text-center">
                        <Spinner animation="border" />
                    </Col>
                </Row>
            ) : (
                <>
                        <LinkKpiOverview
                            clicksPerPath={adjustedKpiClicksPerPath}
                            badClicksPerPath={adjustedKpiBadClicksPerPath}
                            impressionsPerPath={serverKpiImpressionsPerPath}
                            transactions={serverKpiTransactions}
                            commissionAdjustment={cpcAdjustments.total}
                            currency={user?.settings.currency || ''}
                        />
                    <Row>

                        <Col xs={12}>
                            <ClickDataTable
                                title="Page click report"
                                data={filteredPathOverview}
                                    columns={[
                                        { name: 'Page', selector: row => row.domain + row.path, sortable: true, width: '40%', wrap: true },
                                        { name: 'Page views', id: 'PageViews', selector: row => row.impressions, format: row => formatNumberLocale(row.impressions, 0), sortable: true, width: '120px' },
                                        { name: 'CTR', selector: row => row.impressions > 0 ? row.total / row.impressions : 0, format: row => `${formatNumberLocale(row.impressions > 0 ? (row.total / row.impressions) * 100 : 0, 1)}%`, sortable: true },
                                        { name: 'Automated Clicks', selector: row => row.tracked, format: row => formatNumberLocale(row.tracked, 0), sortable: true, id: 'NPClicks', width: '150px' },
                                        { name: 'Total Clicks', selector: row => row.total, format: row => formatNumberLocale(row.total, 0), sortable: true, id: 'Total', width: '120px' },
                                        { name: 'CR', selector: row => row.total > 0 ? row.totalTransactions / row.total : 0, format: row => `${formatNumberLocale(row.total > 0 ? (row.totalTransactions / row.total) * 100 : 0, 2)}%`, sortable: true },
                                        { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, width:'120px' },
                                        { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true, width:'120px' },
                                        { name: 'EPC', selector: row => row.total > 0 ? row.totalCommission / row.total : 0, format: row => formatNumber(row.total > 0 ? row.totalCommission / row.total : 0, user?.settings.currency || ''), sortable: true },
                                        { name: 'EPV', selector: row => row.impressions > 0 ? row.totalCommission / row.impressions : 0, format: row => formatNumber(row.impressions > 0 ? row.totalCommission / row.impressions : 0,user?.settings.currency || ''), sortable: true, width: '120px'},
                                    ]}
                                id='PageViews'
                            />
                        </Col>

                       <Col xs={12}>
                            <ClickDataTable
                                title="Link click report"
                                data={linkOverviewRows}
                                paginationPerPage={25}
                                paginationRowsPerPageOptions={[25, 50, 100, 200]}
                                columns={[
                                    { name: 'Target URL', selector: row => row.url, sortable: true, wrap:true, width:'40%' },
                                    { name: 'Automated Clicks', selector: row => row.NPClicks, format: row => formatNumberLocale(row.NPClicks, 0), sortable: true, id: 'NPClicks', width: '150px' },
                                    { 
                                        name: 'Total Clicks', 
                                        selector: row => (row.NPClicks || 0) + (row.Clicks || 0), 
                                        format: row => formatNumberLocale((row.NPClicks || 0) + (row.Clicks || 0), 0), 
                                        sortable: true 
                                    },
                                    { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, width:'120px' },
                                    { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true },
                                    {name: 'EPC', selector: row => row.totalCommission / (row.NPClicks || 0), format: row => formatNumber((row.totalCommission / (row.NPClicks || 0)), user?.settings.currency || ''), sortable: true}
                                ]}
                                id='NPClicks'
                            />
                        </Col>



                        <Col xs={12}>
                            <ClickDataTable
                                title="Campaign overview"
                                data={campaignOverviewRows}
                                columns={[
                                    { name: 'Name', selector: row => row.name, sortable: true },
                                    { name: 'Network', selector: row => row.network, sortable: true },
                                    { name: 'Publisher', selector: row => row.publisherName, sortable: true },
                                    { name: 'Automated Clicks', selector: row => row.clicks, format: row => formatNumberLocale(row.clicks, 0), sortable: true, id: 'Clicks', width: '150px' },
                                    { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, width:'120px' },
                                    { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true },
                                    {name: 'EPC', selector: row => row.totalCommission / (row.clicks || 0), format: row => formatNumber((row.totalCommission / (row.clicks || 0)), user?.settings.currency || ''), sortable: true}
                                ]}
                                id='Clicks'
                            />
                        </Col>

                        <Col xs={12}>
                            {subPathDrillPrefix && (() => {
                                const crumbs = buildBreadcrumbs(subPathDrillPrefix)
                                return (
                                    <div className="d-flex align-items-center gap-1 small px-1 mb-1">
                                        {crumbs.map((crumb, i) => {
                                            const isLast = i === crumbs.length - 1
                                            return (
                                                <React.Fragment key={i}>
                                                    {i > 0 && <span className="text-muted">/</span>}
                                                    {isLast ? (
                                                        <span className="text-dark fw-semibold">{crumb.label}</span>
                                                    ) : (
                                                        <button
                                                            className="btn btn-link btn-sm p-0 text-muted text-decoration-none"
                                                            onClick={() => setSubPathDrillPrefix(crumb.prefix)}
                                                        >
                                                            {crumb.label}
                                                        </button>
                                                    )}
                                                </React.Fragment>
                                            )
                                        })}
                                    </div>
                                )
                            })()}
                            <div style={{ opacity: isSubPathTransitioning ? 0.5 : 1, transition: 'opacity 0.15s' }}>
                            <ClickDataTable
                                title="Sub-path overview"
                                data={subPathOverview}
                                columns={[
                                    { name: 'Page', selector: row => row.page, sortable: true, width: '40%', wrap: true },
                                    { name: 'Page views', id: 'PageViews', selector: row => row.impressions, format: row => formatNumberLocale(row.impressions, 0), sortable: true, width: '120px' },
                                    { name: 'CTR', selector: row => row.impressions > 0 ? row.total / row.impressions : 0, format: row => `${formatNumberLocale(row.impressions > 0 ? (row.total / row.impressions) * 100 : 0, 1)}%`, sortable: true },
                                    { name: 'Automated Clicks', selector: row => row.tracked, format: row => formatNumberLocale(row.tracked, 0), sortable: true, id: 'NPClicks', width: '150px' },
                                    { name: 'Total Clicks', selector: row => row.total, format: row => formatNumberLocale(row.total, 0), sortable: true, id: 'Total', width: '120px' },
                                    { name: 'CR', selector: row => row.total > 0 ? row.totalTransactions / row.total : 0, format: row => `${formatNumberLocale(row.total > 0 ? (row.totalTransactions / row.total) * 100 : 0, 2)}%`, sortable: true },
                                    { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, width:'120px' },
                                    { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true, width:'120px' },
                                    { name: 'EPC', selector: row => row.total > 0 ? row.totalCommission / row.total : 0, format: row => formatNumber(row.total > 0 ? row.totalCommission / row.total : 0, user?.settings.currency || ''), sortable: true },
                                    { name: 'EPV', selector: row => row.impressions > 0 ? row.totalCommission / row.impressions : 0, format: row => formatNumber(row.impressions > 0 ? row.totalCommission / row.impressions : 0,user?.settings.currency || ''), sortable: true, width: '120px'},
                                ]}
                                id='PageViews'
                                onRowClicked={subPathDrillLevel < 4
                                    ? (row) => setSubPathDrillPrefix(row.page)
                                    : undefined}
                                pointerOnHover={subPathDrillLevel < 4}
                            />
                            </div>
                        </Col>

                        <Col xs={12}>
                            <ClickDataTable
                                title="Domain overview"
                                data={filteredDomainOverview.map(d => ({
                                    domain: d.domain,
                                    NPClicks: d.tracked || 0,      // Mindst 0
                                    Clicks: d.untracked || 0,      // Mindst 0
                                    totalTransactions: d.totalTransactions || 0,
                                    totalCommission: d.totalCommission || 0,
                                    impressions: d.impressions || 0

                                }))}
                                columns={[
                                    { name: 'Domain', selector: row => row.domain, sortable: true },
                                    { name: 'Page views', id: 'PageViews', selector: row => row.impressions, format: row => formatNumberLocale(row.impressions, 0), sortable: true },
                                    { name: 'CTR', selector: row => row.impressions > 0 ? row.Clicks / row.impressions : 0, format: row => `${formatNumberLocale(row.impressions > 0 ? (row.Clicks / row.impressions) * 100 : 0, 1)}%`, sortable: true },
                                    { name: 'Automated Clicks', id: 'NPClicks', selector: row => row.NPClicks, format: row => formatNumberLocale(row.NPClicks, 0), sortable: true, width: '150px' },
                                    { 
                                        name: 'Total Clicks', 
                                        selector: row => (row.NPClicks || 0) + (row.Clicks || 0), 
                                        format: row => formatNumberLocale((row.NPClicks || 0) + (row.Clicks || 0), 0), 
                                        sortable: true, 
                                        id:'Total'
                                    },
                                    { name: 'CR', selector: row => row.Clicks > 0 ? row.totalTransactions / row.Clicks : 0, format: row => `${formatNumberLocale(row.Clicks > 0 ? (row.totalTransactions / row.Clicks) * 100 : 0, 2)}%`, sortable: true },
                                    { name: 'Conversions', selector: row => row.totalTransactions, format: row => formatNumberLocale(row.totalTransactions, 0), sortable: true, width:'120px' },
                                    { name: 'Commission', selector: row => row.totalCommission, format: row => formatNumber(row.totalCommission, user?.settings.currency || ''), sortable: true },
                                    {name: 'EPC', selector: row => row.totalCommission / (row.NPClicks || 0), format: row => formatNumber((row.totalCommission / (row.NPClicks || 0)), user?.settings.currency || ''), sortable: true},
                                    {name: 'EPV', selector: row => row.totalCommission / (row.impressions || 0), format: row => formatNumber((row.totalCommission / (row.impressions || 0)), user?.settings.currency || ''), sortable: true}
                                ]}
                                id='PageViews'
                                height="200px"
                            />
                        </Col>

                    </Row>
                </>
            )}
        </Container>
    )
}

export default LinkScriptDashboard
